import React, { useEffect, useMemo, useState } from "react";
import { Accordion, Button, Heading, Notification, Select, Spinner } from "@telia/teddy";
import HorizontalRule from "../../../components/components/atoms/HorizontalRule";
import { useSelector, useDispatch } from "react-redux";
import DeliveryAddressEditor from "./DeliveryAddressEditor";
import SignatureMethodsModal from "./SignatureMethodsModal";
import PAGES from "../../../../../constants/pages";
import { RootState } from "../../../../../models";
import { useViewport } from "../../../checkout/hooks";
import { getFlattenedCartContent, getNumberOfItemsInCart } from "../../../checkout/state/selectors/cartSelectors";
import { isNewPhysicalSimRequired as isNewPhysicalSimRequiredSelector } from "../../../checkout/state/selectors/combinedSelectors";
import { isTypeLoading } from "../../../loading/state/selectors";
import ConnectedShoppingCart from "../../../navigation/components/organisms/ConnectedShoppingCart/ConnectedShoppingCart";
import { Header } from "../../components/atoms/Header";
import ContactCard from "../../components/molecules/ContactCard/ContactCard";
import { NextButton } from "../../components/molecules/ContinueSection/ContinueSection";
import Status from "../../components/molecules/Status/Status";
import { submitOrder, fetchSignatureMethods, updateSelectedAgent, cancelConflictingOrder } from "../../state/actions";
import { RETAILER_SUBMIT_ORDER } from "../../state/actionTypes";
import {
    getCustomerCreditCheck,
    getCustomerExistingAccounts,
    getCustomerLegalOwnerIndividual,
    getIsPrepaid,
} from "../../state/selectors/customerSelectors";
import { requiresCreditCheck as requiresCreditCheckSelector } from "../../state/selectors/creditCheckSelectors";
import { getFormById } from "../../state/selectors/formSelectors";
import { getSalesAgent, getSalesAgents } from "../../state/selectors/selectors";
import { getFwaSelectedPackageDetails } from "../../state/selectors/fwaSelectors";
import { sendFwaSigningSms } from "../../fwaApi";
import FwaBankIdModal from "./FwaBankIdModal";
import FwaPackageSummary from "./FwaPackageSummary";
import OrderCollisionModal from "./OrderCollisionModal";
import "./Summary.less";
import {
    isCustomerStepValidated,
    isEquipmentStepValidated,
    isSubscriptionStepValidated,
} from "../../state/selectors/validationSelectors";
import { getSubscriptions } from "../../../checkout/state/selectors/subscriptionsSelectors";
import { hasDisplayableMessage } from "../../utils";
import * as AT from "../../state/actionTypes";
import TEXT from "../../text-consts";
import { useOrderValidation } from "./useOrderValidation";
import ValidationMessages from "../../components/molecules/ValidationMessages/ValidationMessages";
import { useQueryClient } from "@tanstack/react-query";
import CancelOrderModal from "../../components/organisms/ShoppingCart/CancelOrderModal";

const Summary = () => {
    const dispatch = useDispatch();
    const [error, setError] = useState<string | null>(null);
    const queryClient = useQueryClient();
    const [isSignatureMethodsModalOpen, setIsSignatureMethodsModalOpen] = useState(false);
    const [isCheckingSignMethods, setIsCheckingSignMethods] = useState(false);
    const [isSendingFwaSms, setIsSendingFwaSms] = useState(false);
    const [fwaBankIdModalOpen, setFwaBankIdModalOpen] = useState(false);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const { width } = useViewport();
    const shouldShowCart = width < 1232;
    const numberOfCartItems = useSelector((state: RootState) => getNumberOfItemsInCart(state));
    const totalCartItemsCount = useSelector((state: RootState) => getFlattenedCartContent(state).length);
    const {
        data: orderValidation,
        isLoading: isValidating,
        isError: isValidationError,
        refetch: refetchOrderValidation,
    } = useOrderValidation(numberOfCartItems > 0);
    const customerStepValidation = useSelector((state: RootState) => isCustomerStepValidated(state));
    const subscriptionStepValidation = useSelector((state: RootState) => isSubscriptionStepValidated(state));
    const equipmentStepValidation = useSelector((state: RootState) => isEquipmentStepValidated(state));
    const legalOwner = useSelector((state: RootState) => getCustomerLegalOwnerIndividual(state));
    const creditCheck = useSelector((state: RootState) => getCustomerCreditCheck(state));
    const isPrepaid = useSelector((state: RootState) => getIsPrepaid(state));
    const existingAccounts = useSelector((state: RootState) => getCustomerExistingAccounts(state));
    const isExistingCustomer = existingAccounts.length > 0;
    const salesAgent = useSelector((state: RootState) => getSalesAgent(state));
    const salesAgents = useSelector((state: RootState) => getSalesAgents(state));
    const isChangingSelectedAgent = useSelector((state: RootState) =>
        isTypeLoading(state, AT.RETAILER_UPDATE_SELECTED_AGENT),
    );

    const hasMountedRef = React.useRef(false);
    const hasRedirectedToReceiptRef = React.useRef(false);
    useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }
        queryClient.resetQueries({ queryKey: ["orderValidation"] });
    }, [queryClient, totalCartItemsCount]);

    useEffect(() => {
        if (hasRedirectedToReceiptRef.current) return;
        const state = orderValidation?.orderState;
        const orderRef = orderValidation?.orderRef;
        const shouldRedirect = !!orderRef && ["PROCESSING", "FINALIZED", "COMPLETED"].includes(state ?? "");

        if (shouldRedirect) {
            hasRedirectedToReceiptRef.current = true;
            window.location.assign(`${PAGES.RETAILER.ORDER.RECEIPT.URL}?orderRef=${orderRef}`);
        }
    }, [orderValidation?.orderRef, orderValidation?.orderState]);

    const shouldShowDeliveryAddressChange = useSelector((state: RootState) => isNewPhysicalSimRequiredSelector(state));
    const identificationForm = useSelector((state: RootState) =>
        getFormById(state, "customerIdentificationForm" as any),
    );
    const isSubmitting = useSelector((state: RootState) => isTypeLoading(state, RETAILER_SUBMIT_ORDER));
    const fwaSelectedPackageDetails = useSelector((state: RootState) => getFwaSelectedPackageDetails(state));
    const subscriptions = useSelector((state: RootState) => getSubscriptions(state));
    const requiresCreditCheck = useSelector((state: RootState) => requiresCreditCheckSelector(state));
    const hasPrepaidProductUser = Object.values(subscriptions).some(
        (sub) => sub.productUser?.firstName && sub.productUser?.lastName,
    );
    const legalOwnerFullName = `${legalOwner?.firstName || ""} ${legalOwner?.lastName || ""}`.trim();
    const legalOwnerAddress = legalOwner?.street || "";
    const legalOwnerCity = legalOwner?.city || "";
    const legalOwnerEmail = legalOwner?.email || "";
    const legalOwnerPhone = legalOwner?.phone || "";

    const sortedAgents = useMemo(
        () =>
            [...salesAgents].sort((a, b) =>
                `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, "no-NB"),
            ),
        [salesAgents],
    );
    const salesAgentFullName = `${salesAgent?.firstName || ""} ${salesAgent?.lastName?.charAt(0) || ""}`.trim();
    const currentOrderSalesAgentName = `${salesAgent?.firstName || ""} ${salesAgent?.lastName || ""}`.trim();

    // Determine identification status
    const identificationMethod = identificationForm?.values?.selectedIdentificationMethod;
    const hasIdentification = Boolean(identificationMethod);
    const canCustomerProceed = isPrepaid
        ? hasPrepaidProductUser &&
          (subscriptionStepValidation?.status === "success" || !subscriptionStepValidation) &&
          (equipmentStepValidation?.status === "success" || !equipmentStepValidation)
        : customerStepValidation?.status === "success" &&
          (subscriptionStepValidation?.status === "success" || !subscriptionStepValidation) &&
          (equipmentStepValidation?.status === "success" || !equipmentStepValidation);

    const handleSubmitOrder = async () => {
        const result = await dispatch(submitOrder());
        // On success, navigate to receipt page with orderRef
        if (result.orderRef) {
            window.location.assign(`${PAGES.RETAILER.ORDER.RECEIPT.URL}?orderRef=${result.orderRef}`);
        }
    };

    const handleSendFwaSms = async () => {
        setError(null);
        setIsSendingFwaSms(true);
        try {
            await sendFwaSigningSms();
            setFwaBankIdModalOpen(true);
        } catch (e) {
            setError(
                hasDisplayableMessage(e)
                    ? e.displayableMessage
                    : e instanceof Error
                      ? e.message
                      : TEXT.ERRORS.SUBMIT_ERROR,
            );
        } finally {
            setIsSendingFwaSms(false);
        }
    };

    const handleAgentChange = async (agentId: string) => {
        const agent = sortedAgents.find((a) => `${a.id}` === `${agentId}`);
        if (!agent) return;
        dispatch(updateSelectedAgent(agentId));
    };

    const handleCompleteOrder = async () => {
        if (fwaSelectedPackageDetails) {
            await handleSendFwaSms();
            return;
        }
        setError(null);
        setIsCheckingSignMethods(true);
        try {
            const result = await dispatch(fetchSignatureMethods());
            const methods = result?.signatureMethods || [];
            if (methods.length > 0) {
                setIsSignatureMethodsModalOpen(true);
            } else {
                await handleSubmitOrder();
            }
        } catch (e) {
            setError(
                hasDisplayableMessage(e)
                    ? e.displayableMessage
                    : e instanceof Error
                      ? e.message
                      : TEXT.ERRORS.SUBMIT_ERROR,
            );
        } finally {
            setIsCheckingSignMethods(false);
        }
    };

    const collisionOrder = orderValidation?.collisionOrder ?? null;

    const handleCancelConflictingOrder = async () => {
        if (!collisionOrder) {
            return;
        }
        try {
            await dispatch(cancelConflictingOrder(collisionOrder.orderId));
            await refetchOrderValidation();
        } catch (e) {
            throw hasDisplayableMessage(e)
                ? e.displayableMessage
                : e instanceof Error
                  ? e.message
                  : TEXT.ERRORS.SUBMIT_ERROR;
        }
    };

    const [showEditAddress, setShowEditAddress] = useState(false);

    return (
        <div className="retailer-dashboard-wrapper">
            <Header text="Oppsummering" />
            <div className="retailer-main-section">
                <div className="retailer-summary-contact-cards">
                    <div className="retailer-summary-contact-card-section">
                        <Heading as="h2" variant="title-100" mb="50">
                            Kunde
                        </Heading>
                        <ContactCard
                            name={legalOwnerFullName}
                            address={legalOwnerAddress}
                            addressContent={
                                shouldShowDeliveryAddressChange ? (
                                    <DeliveryAddressEditor
                                        showEditForm={showEditAddress}
                                        onToggleEdit={() => setShowEditAddress(false)}
                                    />
                                ) : undefined
                            }
                            city={legalOwnerCity}
                            contactPhone={legalOwnerPhone}
                            contactEmail={legalOwnerEmail}
                            showTriggerButton={shouldShowDeliveryAddressChange && !showEditAddress}
                            triggerInsideAddress={true}
                            triggerVariant="link"
                            triggerText="Endre leveringsadresse"
                            triggerFunction={() => setShowEditAddress(!showEditAddress)}
                        />
                    </div>
                    {salesAgent && (
                        <div className="retailer-summary-contact-card-section">
                            <Heading as="h2" variant="title-100" mb="50">
                                Salgsagent
                            </Heading>
                            <ContactCard name={salesAgentFullName} address={salesAgent?.dealerName ?? ""} />
                            {sortedAgents.length > 0 && (
                                <>
                                    <Heading
                                        className="retailer-summary-contact-card-section__dropdown-title"
                                        as="h3"
                                        variant="title-100"
                                        mb="50"
                                    >
                                        Endre agent
                                    </Heading>
                                    {isChangingSelectedAgent ? (
                                        <Spinner size="sm" />
                                    ) : (
                                        <Select
                                            name="agent"
                                            options={sortedAgents.map((agent) => `${agent.id}`)}
                                            value={`${salesAgent?.id || ""}`}
                                            onValueChange={(value: string) => {
                                                handleAgentChange(value);
                                                return value;
                                            }}
                                        >
                                            <Select.Trigger>
                                                <Select.Value placeholder="Velge..." />
                                            </Select.Trigger>
                                            <Select.Portal>
                                                <Select.Content>
                                                    <Select.Viewport>
                                                        {sortedAgents.map((agent) => (
                                                            <Select.Item key={agent.id} value={`${agent.id}`}>
                                                                {agent.firstName} {agent.lastName}
                                                            </Select.Item>
                                                        ))}
                                                    </Select.Viewport>
                                                </Select.Content>
                                            </Select.Portal>
                                        </Select>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
                <HorizontalRule />
                <div className="retailer-summary-status">
                    <Heading as="h2" variant="title-100" mb="100">
                        Status
                    </Heading>
                    <Status
                        hasCreditCheck={creditCheck?.performed || false}
                        hasIdentification={hasIdentification}
                        hasLegalOwner={!!legalOwnerFullName}
                        identificationMethod={identificationMethod}
                        requiresCreditCheck={requiresCreditCheck}
                        creditCheckDecision={creditCheck?.decision}
                        creditCheckMessage={creditCheck?.message}
                        isExistingCustomer={isExistingCustomer}
                    />
                </div>
                <HorizontalRule />
                {fwaSelectedPackageDetails && <FwaPackageSummary packageDetails={fwaSelectedPackageDetails} />}
                {shouldShowCart && numberOfCartItems > 0 && (
                    <Accordion type="single" collapsible>
                        <Accordion.Item value="cart">
                            <Accordion.Header>
                                <Accordion.Trigger>{`Handlekurv (${numberOfCartItems})`}</Accordion.Trigger>
                            </Accordion.Header>
                            <Accordion.Content>
                                <ConnectedShoppingCart isRetailer useStandalone={true} heading="" />
                            </Accordion.Content>
                        </Accordion.Item>
                    </Accordion>
                )}
                <ValidationMessages
                    isValidating={isValidating}
                    isValidationError={isValidationError}
                    refetchOrderValidation={refetchOrderValidation}
                    orderValidation={orderValidation}
                />
                {!!error && (
                    <Notification type="tile" variant="error" className="error">
                        <Notification.Icon />
                        <Notification.Text>
                            <span dangerouslySetInnerHTML={{ __html: error }} />
                        </Notification.Text>
                    </Notification>
                )}
                <div className="retailer-summary-actions">
                    {!isValidating && (
                        <Button
                            variant="tertiary-purple"
                            onClick={() => setIsCancelModalOpen(true)}
                            disabled={isSubmitting}
                        >
                            Slett ordre
                        </Button>
                    )}
                    <div className="retailer-summary-actions__right">
                        {!isValidating && orderValidation?.validationPassed === true && (
                            <NextButton
                                onClick={handleCompleteOrder}
                                disabled={
                                    isSubmitting ||
                                    isCheckingSignMethods ||
                                    isSendingFwaSms ||
                                    isSignatureMethodsModalOpen ||
                                    (!fwaSelectedPackageDetails && (!canCustomerProceed || numberOfCartItems === 0))
                                }
                                loading={isSubmitting || isCheckingSignMethods || isSendingFwaSms}
                                title="Til signering"
                                tooltip={
                                    fwaSelectedPackageDetails || canCustomerProceed
                                        ? undefined
                                        : customerStepValidation?.message ||
                                          subscriptionStepValidation?.message ||
                                          equipmentStepValidation?.message
                                }
                            />
                        )}
                    </div>
                </div>
            </div>
            {isSignatureMethodsModalOpen && (
                <SignatureMethodsModal
                    isModalOpen={isSignatureMethodsModalOpen}
                    setIsModalOpen={setIsSignatureMethodsModalOpen}
                />
            )}
            <FwaBankIdModal
                isOpen={fwaBankIdModalOpen}
                onClose={() => setFwaBankIdModalOpen(false)}
                onCompleted={(orderRef) => {
                    setFwaBankIdModalOpen(false);
                    window.location.assign(`${PAGES.RETAILER.ORDER.RECEIPT.URL}?orderRef=${orderRef}`);
                }}
            />
            {isCancelModalOpen && <CancelOrderModal onClose={() => setIsCancelModalOpen(false)} />}
            {collisionOrder && (
                <OrderCollisionModal
                    collisionOrder={collisionOrder}
                    errorMessage={orderValidation?.errors?.join(" ")}
                    currentOrderCreationDate={orderValidation?.currentOrderCreationDate}
                    currentOrderId={orderValidation?.orderRef ? `${orderValidation.orderRef}` : "Ikke tilgjengelig"}
                    currentOrderSalesAgentName={currentOrderSalesAgentName || "Ukjent"}
                    salesAgents={salesAgents}
                    onClose={() => {}}
                    onCancelConflictingOrder={handleCancelConflictingOrder}
                />
            )}
        </div>
    );
};

export default Summary;
